import { useCallback, useEffect, useMemo, useState } from "react";
import { Redirect, useLocation, useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card } from "reactstrap";
import CharacterizationRow from "./CharacterizationRow";
import { useRotationPeriods } from "../characterizationArea/useRotationPeriods";
import { useAllRotationPeriods } from "../characterizationArea/useAllRotationPeriods";
import { useCharacterizationViewMode } from "../characterizationArea/useCharacterizationViewMode";
import { getCharacterizationStatus } from "../core/utils/utils";
import {
  CHARACTERIZATION_VIEW_ZONES,
  characterizationLandingPath,
  resolveSuperContainerForZoneSite,
} from "../core/utils/characterizationViewMode";
import { InlineLoader } from "../../../components/loader/CharacterizationLoader";
import PdfExportProgressOverlay from "../../../components/loader/PdfExportProgressOverlay";
import PageHeader from "../../../components/PageHeader";
import {
  PeriodFilterModal,
  TableFilterButton,
  TableFilterClearLink,
  hasActiveClearableFilters,
} from "../../../ui/components/filters";
import StatusFilterModal from "./StatusFilterModal";
import OriginFilterModal from "./OriginFilterModal";
import ZoneFilterModal from "../../settings/client/ZoneFilterModal";
import { exportCsvSummaryFromRows } from "../core/utils/exportCsvSummaryFromRows";
import settingsIcon from "../../../static/assets/Settings.svg";
import ContainerEdit from "../../container/ContainerEdit";
import Pagination from "../../../components/utils/Pagination";
import StandardTable from "../../../ui/components/StandardTable";

const getPeriodOptions = (t) => [
  { label: t("Last 6 months"), value: 6 },
  { label: t("Last 12 months"), value: 12 },
  { label: t("Last 3 months"), value: 3 },
];

const getStatusOptions = (t) => [
  { label: t("All"), value: "all" },
  { label: t("Image en traitement"), value: "ImageEnTraitement" },
  { label: t("A compléter"), value: "ACompleter" },
  { label: t("Correction en traitement"), value: "CorrectionEnTraitement" },
  { label: t("Validated"), value: "Validated" },
  { label: t("Error"), value: "Error" },
];

const RECORD_PER_PAGE = 10;
const STORAGE_KEY_PERIOD = "characterizationList_periodFilter";
const STORAGE_KEY_PERIOD_START = "characterizationList_periodStartDate";
const STORAGE_KEY_PERIOD_END = "characterizationList_periodEndDate";
const STORAGE_KEY_PERIOD_LABEL = "characterizationList_periodDisplayLabel";
const STORAGE_KEY_RECORD_PER_PAGE = "characterizationList_recordPerPage";
const STORAGE_KEY_SEARCH = "characterizationList_searchValue";
const STORAGE_KEY_STATUS = "characterizationList_statusFilter";
const STORAGE_KEY_ORIGIN = "characterizationList_originFilter";
const STORAGE_KEY_ZONE = "characterizationList_zoneFilter";
const VALID_PERIOD_VALUES = [3, 6, 12];
const VALID_RECORD_PER_PAGE = [2, 10, 25, 50, 100];
const VALID_STATUS_VALUES = [
  "all",
  "ImageEnTraitement",
  "ACompleter",
  "CorrectionEnTraitement",
  "Validated",
  "Error",
];

const getFiltersFromQuery = (location) => {
  try {
    const params = new URLSearchParams(location?.search || "");
    const q = params.get("q") || "";
    const status = params.get("status") || "all";
    const origin = (params.get("origin") || "").trim();
    const periodRaw = params.get("period");
    const start = params.get("start");
    const end = params.get("end");
    const label = params.get("label");
    const parsedStatus = VALID_STATUS_VALUES.includes(status) ? status : "all";

    let periodFilter = null;
    let periodStartDate = null;
    let periodEndDate = null;
    let periodDisplayLabel = label || null;

    const periodNum = periodRaw != null ? Number(periodRaw) : NaN;
    if (VALID_PERIOD_VALUES.includes(periodNum)) {
      periodFilter = periodNum;
      periodStartDate = null;
      periodEndDate = null;
    } else if (start && end) {
      periodFilter = null;
      periodStartDate = start;
      periodEndDate = end;
    }

    const hasPeriod =
      VALID_PERIOD_VALUES.includes(periodFilter) ||
      (periodStartDate != null && periodEndDate != null);

    return {
      searchValue: q,
      statusFilter: parsedStatus,
      originFilter: origin,
      periodFilter,
      periodStartDate,
      periodEndDate,
      periodDisplayLabel,
      hasAnyFilterInQuery:
        Boolean(q.trim()) ||
        parsedStatus !== "all" ||
        Boolean(origin) ||
        hasPeriod,
    };
  } catch (_) {
    return {
      searchValue: "",
      statusFilter: "all",
      originFilter: "",
      periodFilter: null,
      periodStartDate: null,
      periodEndDate: null,
      periodDisplayLabel: null,
      hasAnyFilterInQuery: false,
    };
  }
};

const getStoredPeriodState = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_PERIOD);
    const start = localStorage.getItem(STORAGE_KEY_PERIOD_START);
    const end = localStorage.getItem(STORAGE_KEY_PERIOD_END);
    const label = localStorage.getItem(STORAGE_KEY_PERIOD_LABEL);
    const value = stored !== null ? Number(stored) : null;
    if (VALID_PERIOD_VALUES.includes(value)) {
      return {
        periodFilter: value,
        periodStartDate: null,
        periodEndDate: null,
        periodDisplayLabel: label || null,
      };
    }
    if (start && end) {
      const startDate = new Date(start);
      const endDate = new Date(end);
      if (
        !Number.isNaN(startDate.getTime()) &&
        !Number.isNaN(endDate.getTime())
      ) {
        return {
          periodFilter: null,
          periodStartDate: start,
          periodEndDate: end,
          periodDisplayLabel: label || null,
        };
      }
    }
  } catch (_) {
    // ignore
  }
  return null;
};

/** Période demandée par la navigation (bouton "Toutes les caractérisations du client") : state ou query params */
const getPeriodFromNavigation = (location, t) => {
  const state = location?.state || {};
  let start = state.periodStartDate;
  let end = state.periodEndDate;
  let label = state.periodDisplayLabel;
  if (!start || !end) {
    const params = new URLSearchParams(location?.search || "");
    if (params.get("period") === "sinceBeginning") {
      start = start ?? params.get("start") ?? null;
      end = end ?? params.get("end") ?? null;
      if (!label) label = t("Since the beginning");
    }
  }
  if (start && end) {
    return {
      periodFilter: null,
      periodStartDate: start,
      periodEndDate: end,
      periodDisplayLabel: label || t("Since the beginning"),
    };
  }
  return null;
};

const getStoredRecordPerPage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_RECORD_PER_PAGE);
    const num = stored !== null ? Number(stored) : NaN;
    if (VALID_RECORD_PER_PAGE.includes(num)) return num;
  } catch (_) {
    // ignore
  }
  return null;
};

const getStoredSearchValue = () => {
  try {
    return localStorage.getItem(STORAGE_KEY_SEARCH) || "";
  } catch (_) {
    return "";
  }
};

const getStoredStatusFilter = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_STATUS) || "all";
    return VALID_STATUS_VALUES.includes(stored) ? stored : "all";
  } catch (_) {
    return "all";
  }
};

const getStoredZoneFilter = () => {
  try {
    return localStorage.getItem(STORAGE_KEY_ZONE) || "all";
  } catch (_) {
    return "all";
  }
};

const normalizeOriginName = (name) => String(name || "").trim();

const isSelectableOriginName = (name) => {
  const normalized = normalizeOriginName(name);
  return normalized !== "" && normalized !== "-";
};

const getStoredOriginFilter = () => {
  try {
    return normalizeOriginName(localStorage.getItem(STORAGE_KEY_ORIGIN) || "");
  } catch (_) {
    return "";
  }
};

const formatTitleCase = (text) => {
  if (!text) return "";
  return text
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};
const parseClient = (payload) => {
  if (!payload) {
    return { name: "-", address: "-" };
  }
  try {
    return JSON.parse(payload);
  } catch (error) {
    return { name: "-", address: "-" };
  }
};
const getLastUpdateDate = (row) => {
  if (row?.lastUpdate) {
    return new Date(row.lastUpdate);
  }
  if (row?.updatedAt) {
    return new Date(row.updatedAt);
  }
  if (row?.stop) {
    return new Date(row.stop);
  }
  return null;
};

const TableHeaderCell = ({ label, columnKey, sortConfig, onSort }) => {
  const isActive = sortConfig.column === columnKey;
  const directionIcon = isActive
    ? sortConfig.direction === "asc"
      ? "fa-sort-up"
      : "fa-sort-down"
    : "fa-sort";
  return (
    <div
      className="table-standard-header-cell"
      style={{ cursor: "pointer" }}
      onClick={() => onSort(columnKey)}
      onKeyDown={(e) => e.key === "Enter" && onSort(columnKey)}
      role="button"
      tabIndex={0}
    >
      {label}
      <span className="sort-icon">
        <i className={`fas ${directionIcon}`} aria-hidden />
      </span>
    </div>
  );
};
const CharacterizationList = ({ landingPath }) => {
  const location = useLocation();
  const history = useHistory();
  const { id: routeZoneId } = useParams();
  const { t, i18n } = useTranslation();
  const isAllReportsList = !routeZoneId;
  const {
    zone,
    superContainer,
    containers,
    clientFilter,
    periodStartDate: statePeriodStart,
    periodEndDate: statePeriodEnd,
    periodDisplayLabel: statePeriodLabel,
  } = location.state || {};
  const defaultPeriodOpts = getPeriodOptions(t);
  const storedPeriod = getStoredPeriodState();
  const queryFilters = useMemo(() => getFiltersFromQuery(location), [location]);
  const periodFromNav = useMemo(
    () => getPeriodFromNavigation(location, t),
    [location, t],
  );
  const [searchValue, setSearchValue] = useState(() => {
    if (queryFilters.searchValue) return queryFilters.searchValue;
    return getStoredSearchValue();
  });
  const [periodFilter, setPeriodFilter] = useState(() => {
    if (
      queryFilters.hasAnyFilterInQuery &&
      (VALID_PERIOD_VALUES.includes(queryFilters.periodFilter) ||
        (queryFilters.periodStartDate && queryFilters.periodEndDate))
    ) {
      return queryFilters.periodFilter;
    }
    if (periodFromNav) return periodFromNav.periodFilter;
    if (storedPeriod?.periodStartDate && storedPeriod?.periodEndDate)
      return null;
    if (storedPeriod && VALID_PERIOD_VALUES.includes(storedPeriod.periodFilter))
      return storedPeriod.periodFilter;
    return defaultPeriodOpts[0].value;
  });
  const [statusFilter, setStatusFilter] = useState(() => {
    if (queryFilters.hasAnyFilterInQuery) return queryFilters.statusFilter;
    return getStoredStatusFilter();
  });
  const [originFilter, setOriginFilter] = useState(() => {
    if (isSelectableOriginName(queryFilters.originFilter)) {
      return normalizeOriginName(queryFilters.originFilter);
    }
    if (isSelectableOriginName(clientFilter)) {
      return normalizeOriginName(clientFilter);
    }
    if (queryFilters.hasAnyFilterInQuery) return "";
    return getStoredOriginFilter();
  });
  const [zoneFilter, setZoneFilter] = useState(() => getStoredZoneFilter());
  const [isZoneModalOpen, setIsZoneModalOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    column: "start",
    direction: "desc",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [recordPerPage, setRecordPerPage] = useState(
    () => getStoredRecordPerPage() ?? RECORD_PER_PAGE,
  );
  const [resultPage, setResultPage] = useState(0);
  const [isPeriodModalOpen, setIsPeriodModalOpen] = useState(false);
  const [periodDisplayLabel, setPeriodDisplayLabel] = useState(
    queryFilters.hasAnyFilterInQuery
      ? queryFilters.periodDisplayLabel ??
          (queryFilters.periodFilter != null
            ? defaultPeriodOpts.find(
                (o) => o.value === queryFilters.periodFilter,
              )?.label ?? defaultPeriodOpts[0].label
            : statePeriodLabel ?? storedPeriod?.periodDisplayLabel ?? "")
      : periodFromNav?.periodDisplayLabel ??
          statePeriodLabel ??
          storedPeriod?.periodDisplayLabel ??
          defaultPeriodOpts[0].label,
  );
  const [periodStartDate, setPeriodStartDate] = useState(
    queryFilters.hasAnyFilterInQuery
      ? queryFilters.periodStartDate
      : periodFromNav?.periodStartDate ??
          statePeriodStart ??
          storedPeriod?.periodStartDate ??
          null,
  );
  const [periodEndDate, setPeriodEndDate] = useState(
    queryFilters.hasAnyFilterInQuery
      ? queryFilters.periodEndDate
      : periodFromNav?.periodEndDate ??
          statePeriodEnd ??
          storedPeriod?.periodEndDate ??
          null,
  );
  useEffect(
    () => {
      if (periodFilter != null && VALID_PERIOD_VALUES.includes(periodFilter)) {
        const opts = getPeriodOptions(t);
        setPeriodDisplayLabel(
          opts.find((o) => o.value === periodFilter)?.label ?? opts[0].label,
        );
      }
    },
    // eslint-disable-next-line
    [i18n.language, periodFilter],
  );
  // Persister la période demandée par la navigation (ex: "Toutes les caractérisations du client")
  useEffect(() => {
    if (!periodFromNav) return;
    const {
      periodStartDate: start,
      periodEndDate: end,
      periodDisplayLabel: label,
    } = periodFromNav;
    setCurrentPage(1);
    try {
      localStorage.setItem(STORAGE_KEY_PERIOD, "");
      localStorage.setItem(STORAGE_KEY_PERIOD_START, start);
      localStorage.setItem(STORAGE_KEY_PERIOD_END, end);
      localStorage.setItem(STORAGE_KEY_PERIOD_LABEL, label || "");
    } catch (_) {
      // ignore
    }
  }, [periodFromNav]);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isOriginModalOpen, setIsOriginModalOpen] = useState(false);
  const [isExportingSummary, setIsExportingSummary] = useState(false);
  const [exportProgress, setExportProgress] = useState({
    percent: 0,
    label: "",
  });
  const [statusDisplayLabel, setStatusDisplayLabel] = useState(
    getStatusOptions(t).find((opt) => opt.value === queryFilters.statusFilter)
      ?.label || getStatusOptions(t)[0].label,
  );
  const [modalEdit, setModalEdit] = useState(false);
  const [formData, setFormData] = useState({});

  const { periodMaterialStart, periodMaterialStop } = useMemo(() => {
    const now = new Date();
    now.setHours(23, 59, 59, 999);
    if (periodStartDate && periodEndDate) {
      const start = new Date(periodStartDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(periodEndDate);
      end.setHours(23, 59, 59, 999);
      return {
        periodMaterialStart: start.toISOString(),
        periodMaterialStop: end.toISOString(),
      };
    }
    if (periodFilter === null || periodFilter === undefined) {
      return {
        periodMaterialStart: null,
        periodMaterialStop: now.toISOString(),
      };
    }
    const months = Number(periodFilter);
    if (isNaN(months) || months <= 0) {
      const defaultStart = new Date(now);
      defaultStart.setMonth(defaultStart.getMonth() - 6);
      defaultStart.setHours(0, 0, 0, 0);
      return {
        periodMaterialStart: defaultStart.toISOString(),
        periodMaterialStop: now.toISOString(),
      };
    }

    const startDate = new Date(now);
    if (months < 1) {
      const days = Math.ceil(months * 30);
      startDate.setDate(startDate.getDate() - days);
    } else {
      startDate.setMonth(startDate.getMonth() - Math.ceil(months));
    }
    startDate.setHours(0, 0, 0, 0);
    return {
      periodMaterialStart: startDate.toISOString(),
      periodMaterialStop: now.toISOString(),
    };
  }, [periodFilter, periodStartDate, periodEndDate]);
  const { periods: zoneRotationPeriods = [], isLoading: isZonePeriodsLoading } =
    useRotationPeriods({
      site: isAllReportsList ? null : zone,
      superContainerId: superContainer?._id ?? null,
      i18n,
      periodMaterialStart,
      periodMaterialStop,
    });
  const {
    periods: allRotationPeriods = [],
    superContainers: allSuperContainers,
    containers: allContainers,
    isLoading: isAllPeriodsLoading,
  } = useAllRotationPeriods({
    enabled: isAllReportsList,
    periodMaterialStart,
    periodMaterialStop,
  });
  const rotationPeriods = isAllReportsList
    ? allRotationPeriods
    : zoneRotationPeriods;
  const isLoading = isAllReportsList
    ? isAllPeriodsLoading
    : isZonePeriodsLoading;
  const normalizedRows = useMemo(
    () =>
      rotationPeriods.map((period) => {
        const calculatedStatus = getCharacterizationStatus(period);
        let wasteCategory = null;
        if (
          period.waste_category &&
          String(period.waste_category).trim() !== ""
        ) {
          try {
            wasteCategory = JSON.parse(period.waste_category);
          } catch {
            wasteCategory = null;
          }
        }
        return {
          ...period,
          status: calculatedStatus,
          clientParsed: parseClient(period.client),
          startDate: period.start ? new Date(period.start) : null,
          lastUpdateDate: getLastUpdateDate(period),
          wasteCategory,
          zoneId: period.zoneId || zone?._id,
          zoneLabel: period.zoneLabel || "",
          zoneSite: period.zoneSite || zone,
        };
      }),
    [rotationPeriods, zone],
  );
  const originOptions = useMemo(() => {
    const byKey = new Map();
    normalizedRows.forEach((row) => {
      const name = normalizeOriginName(row.clientParsed?.name);
      if (!isSelectableOriginName(name)) return;
      const key = name.toLowerCase();
      if (!byKey.has(key)) byKey.set(key, name);
    });
    const selected = normalizeOriginName(originFilter);
    if (isSelectableOriginName(selected)) {
      byKey.set(selected.toLowerCase(), selected);
    }
    return Array.from(byKey.values())
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ value: name, label: name }));
  }, [normalizedRows, originFilter]);
  const zoneOptions = useMemo(() => {
    const byId = new Map();
    normalizedRows.forEach((row) => {
      const id = row.zoneId != null ? String(row.zoneId) : "";
      if (!id) return;
      if (!byId.has(id)) {
        byId.set(id, {
          value: id,
          label: row.zoneLabel || row.zoneSite?.addressName || id,
        });
      }
    });
    return Array.from(byId.values()).sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  }, [normalizedRows]);
  const filteredRows = useMemo(() => {
    const searchLower = searchValue.trim().toLowerCase();
    const originLower = originFilter.trim().toLowerCase();
    return normalizedRows.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) {
        return false;
      }
      if (originLower) {
        const rowOrigin = (row.clientParsed?.name ?? "").trim().toLowerCase();
        if (rowOrigin !== originLower) {
          return false;
        }
      }
      if (
        isAllReportsList &&
        zoneFilter &&
        zoneFilter !== "all" &&
        String(row.zoneId) !== String(zoneFilter)
      ) {
        return false;
      }

      const clientName = row.clientParsed?.name?.toLowerCase() ?? "";
      const clientAddress = row.clientParsed?.address?.toLowerCase() ?? "";

      if (!searchLower) {
        return true;
      }
      return (
        clientName.includes(searchLower) || clientAddress.includes(searchLower)
      );
    });
  }, [
    normalizedRows,
    searchValue,
    statusFilter,
    originFilter,
    zoneFilter,
    isAllReportsList,
  ]);
  const sortedRows = useMemo(() => {
    const sorted = [...filteredRows];
    sorted.sort((a, b) => {
      const multiplier = sortConfig.direction === "asc" ? 1 : -1;
      switch (sortConfig.column) {
        case "status":
          return multiplier * a.status.localeCompare(b.status);
        case "client":
          return (
            multiplier *
            (a.clientParsed?.name ?? "-").localeCompare(
              b.clientParsed?.name ?? "-",
            )
          );
        case "lastUpdate":
          return (
            multiplier *
            ((a.lastUpdateDate?.getTime() ?? 0) -
              (b.lastUpdateDate?.getTime() ?? 0))
          );
        case "start":
        default:
          return (
            multiplier *
            ((a.startDate?.getTime() ?? 0) - (b.startDate?.getTime() ?? 0))
          );
      }
    });
    return sorted;
  }, [filteredRows, sortConfig]);

  const canExportSummaryCsv = sortedRows.length > 0;

  const showClearFilters = hasActiveClearableFilters([
    { value: searchValue, defaultValue: "" },
    { value: statusFilter, defaultValue: "all" },
    { value: originFilter, defaultValue: "" },
    ...(isAllReportsList ? [{ value: zoneFilter, defaultValue: "all" }] : []),
  ]);

  const handleClearFilters = useCallback(() => {
    setSearchValue("");
    setStatusFilter("all");
    setOriginFilter("");
    setZoneFilter("all");
    setCurrentPage(1);
  }, []);

  const handleExportSummaryCsv = async () => {
    if (!canExportSummaryCsv) return;
    setIsExportingSummary(true);
    setExportProgress({ percent: 5, label: t("Loading...") });
    try {
      await exportCsvSummaryFromRows({
        rows: sortedRows.map((period) => ({
          period,
          container: period.zoneSite || zone,
          superContainer: isAllReportsList
            ? resolveSuperContainerForZoneSite(
                allSuperContainers,
                period.zoneSite,
              )
            : superContainer,
        })),
        i18n,
        t,
        onProgress: ({ percent, label }) =>
          setExportProgress({
            percent,
            label: label || t("Loading..."),
          }),
      });
    } catch (error) {
      console.error("Error exporting summary CSV:", error);
    } finally {
      setIsExportingSummary(false);
      setExportProgress({ percent: 0, label: "" });
    }
  };
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * recordPerPage;
    return sortedRows.slice(start, start + recordPerPage);
  }, [currentPage, sortedRows, recordPerPage]);

  useEffect(() => {
    setResultPage(Math.ceil(sortedRows.length / recordPerPage));
  }, [sortedRows.length, recordPerPage]);

  const hasCharacterizations = paginatedRows.length > 0;
  const handleSort = useCallback((column) => {
    setSortConfig((prev) => {
      if (prev.column === column) {
        return {
          column,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { column, direction: "asc" };
    });
  }, []);

  useEffect(() => {
    const selectedOption = getStatusOptions(t).find(
      (opt) => opt.value === statusFilter,
    );
    setStatusDisplayLabel(
      selectedOption?.label || getStatusOptions(t)[0].label,
    );
  }, [statusFilter, i18n.language, t]);

  useEffect(() => {
    const params = new URLSearchParams();
    const trimmedSearch = searchValue.trim();
    if (trimmedSearch) params.set("q", trimmedSearch);
    if (statusFilter && statusFilter !== "all")
      params.set("status", statusFilter);
    const trimmedOrigin = originFilter.trim();
    if (trimmedOrigin) params.set("origin", trimmedOrigin);
    if (
      periodFilter != null &&
      VALID_PERIOD_VALUES.includes(Number(periodFilter))
    ) {
      params.set("period", String(periodFilter));
    } else if (periodStartDate && periodEndDate) {
      params.set("period", "custom");
      params.set("start", periodStartDate);
      params.set("end", periodEndDate);
      if (periodDisplayLabel) params.set("label", periodDisplayLabel);
    }
    const nextSearch = params.toString();
    const currentSearch = (location.search || "").replace(/^\?/, "");
    if (nextSearch !== currentSearch) {
      history.replace({
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
        state: location.state,
      });
    }
    try {
      localStorage.setItem(STORAGE_KEY_SEARCH, searchValue || "");
      localStorage.setItem(STORAGE_KEY_STATUS, statusFilter || "all");
      localStorage.setItem(STORAGE_KEY_ORIGIN, originFilter.trim());
      if (isAllReportsList) {
        localStorage.setItem(STORAGE_KEY_ZONE, zoneFilter || "all");
      }
    } catch (_) {
      // ignore
    }
  }, [
    searchValue,
    statusFilter,
    originFilter,
    zoneFilter,
    isAllReportsList,
    periodFilter,
    periodStartDate,
    periodEndDate,
    periodDisplayLabel,
    history,
    location.pathname,
    location.search,
    location.state,
  ]);
  return (
    <div className="settings-page-wrapper">
      <PageHeader
        titleContent={
          <div className="flex items-center gap-3 flex-wrap">
            <span>
              {isAllReportsList
                ? t("Characterizations")
                : zone?.addressName
                  ? formatTitleCase(zone.addressName)
                  : t("Zone")}
            </span>
            {!isAllReportsList && zone?.positioning && (
              <span
                className="inline-flex items-center justify-center rounded-full text-sm font-bold text-[#2563EB]"
                style={{
                  padding: "4px 12px",
                  height: "30px",
                  background: "#E6ECFD",
                  borderRadius: "1000px",
                }}
              >
                {zone.positioning.charAt(0).toUpperCase() +
                  zone.positioning.slice(1)}
              </span>
            )}
          </div>
        }
        titleContentMain={
          <span>
            {isAllReportsList
              ? t("Characterizations")
              : zone?.addressName
                ? formatTitleCase(zone.addressName)
                : t("Zone")}
          </span>
        }
        titleContentBadges={
          !isAllReportsList && zone?.positioning ? (
            <span
              className="inline-flex items-center justify-center rounded-full text-sm font-bold text-[#2563EB]"
              style={{
                padding: "4px 12px",
                height: "30px",
                background: "#E6ECFD",
                borderRadius: "1000px",
              }}
            >
              {zone.positioning.charAt(0).toUpperCase() +
                zone.positioning.slice(1)}
            </span>
          ) : null
        }
        showBackButton={!isAllReportsList}
        backRoute={landingPath || "/characterization-areas"}
        backLabel={t("Back to Zones")}
        shareButton={
          isAllReportsList ? null : (
            <button
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold text-[#222222] border border-[#d3d3d3] bg-white hover:bg-[#f8f8f8] transition-colors"
              onClick={() => {
                if (zone) {
                  const id = zone._id ?? zone._measurement ?? zone.id;
                  if (id) {
                    history.push(`/settings/containers/${id}`, {
                      container: zone,
                      from: "characterization",
                      returnPath: location.pathname,
                    });
                  } else {
                    setFormData(zone);
                    setModalEdit(true);
                  }
                }
              }}
            >
              <img
                src={settingsIcon}
                alt="settings"
                style={{ width: "16px", height: "16px" }}
              />
              {t("Zone settings")}
            </button>
          )
        }
        actionsMenuItems={
          isAllReportsList
            ? null
            : [
                {
                  label: t("Zone settings"),
                  icon: <img src={settingsIcon} alt="" className="w-4 h-4" />,
                  onClick: () => {
                    if (zone) {
                      const id = zone._id ?? zone._measurement ?? zone.id;
                      if (id) {
                        history.push(`/settings/containers/${id}`, {
                          container: zone,
                          from: "characterization",
                          returnPath: location.pathname,
                        });
                      } else {
                        setFormData(zone);
                        setModalEdit(true);
                      }
                    }
                  },
                },
              ]
        }
      />
      <div className="settings-content-wrapper">
        <div className="settings-list-shell">
          <Card className="table border-0 shadow-none rounded-lg">
            <StandardTable
              fluidColCount={4}
              fixedColWidth={150}
              columnAlignments={["left", "left", "left", "left", "right"]}
              topBar={
                <section className="table-standard-top-bar">
                  <div className="table-standard-top-bar-inner">
                    <div className="flex gap-2 items-center flex-wrap flex-1 min-w-0">
                      <div className="search-input-wrapper">
                        <i
                          className="fas fa-search search-input-icon"
                          aria-hidden
                        />
                        <input
                          type="text"
                          className="search-input"
                          value={searchValue}
                          placeholder={t("Search for a client")}
                          onChange={(event) => {
                            setSearchValue(event.target.value);
                            setCurrentPage(1);
                          }}
                        />
                      </div>
                      <TableFilterButton
                        label={t("Period")}
                        value={
                          periodDisplayLabel || getPeriodOptions(t)[0].label
                        }
                        onClick={() => setIsPeriodModalOpen(true)}
                      />
                      <TableFilterButton
                        label={t("Status")}
                        value={
                          statusDisplayLabel || getStatusOptions(t)[0].label
                        }
                        onClick={() => setIsStatusModalOpen(true)}
                      />
                      <TableFilterButton
                        label={t("Waste origin")}
                        value={
                          originFilter
                            ? originOptions.find(
                                (option) =>
                                  String(option.value).toLowerCase() ===
                                  originFilter.toLowerCase(),
                              )?.label || originFilter
                            : t("All")
                        }
                        onClick={() => setIsOriginModalOpen(true)}
                      />
                      {isAllReportsList ? (
                        <TableFilterButton
                          label={t("Zone")}
                          value={
                            zoneFilter === "all"
                              ? t("All")
                              : zoneOptions.find(
                                  (option) =>
                                    String(option.value) === String(zoneFilter),
                                )?.label || t("All")
                          }
                          onClick={() => setIsZoneModalOpen(true)}
                        />
                      ) : null}
                      <TableFilterClearLink
                        visible={showClearFilters}
                        onClear={handleClearFilters}
                      />
                    </div>
                    <button
                      type="button"
                      className="char-button char-button-primary"
                      disabled={isExportingSummary || !canExportSummaryCsv}
                      onClick={handleExportSummaryCsv}
                      style={{ whiteSpace: "nowrap" }}
                    >
                      <i className="fas fa-file-csv mr-2" aria-hidden />
                      {t("Export CSV summary")}
                    </button>
                  </div>
                </section>
              }
              header={
                <div className="table-standard-header">
                  <TableHeaderCell
                    label={t("Name")}
                    columnKey="start"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                  <TableHeaderCell
                    label={t("Status")}
                    columnKey="status"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                  <TableHeaderCell
                    label={t("Waste origin")}
                    columnKey="client"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                  <TableHeaderCell
                    label={t("Last modification")}
                    columnKey="lastUpdate"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                  <div className="table-standard-header-cell" />
                </div>
              }
              bottom={
                sortedRows.length > 0 ? (
                  <div className="table-standard-bottom table-pagination-bottom">
                    <Pagination
                      totalEntry={sortedRows.length}
                      recordPerPage={recordPerPage}
                      setRecordPerPage={(value) => {
                        setRecordPerPage(value);
                        try {
                          localStorage.setItem(
                            STORAGE_KEY_RECORD_PER_PAGE,
                            String(value),
                          );
                        } catch (_) {
                          // ignore
                        }
                      }}
                      currentPage={currentPage}
                      setCurrentPage={setCurrentPage}
                      resultPage={resultPage}
                      setResultPage={setResultPage}
                    />
                  </div>
                ) : null
              }
            >
              {isLoading ? (
                <div className="table-standard-row">
                  <div
                    className="table-standard-row-cell justify-center min-h-[200px]"
                    style={{ gridColumn: "1 / -1" }}
                  >
                    <InlineLoader height="200px" />
                  </div>
                </div>
              ) : hasCharacterizations ? (
                paginatedRows.map((row) => (
                  <CharacterizationRow
                    key={`${row.zoneId || ""}-${row.start || row._id}`}
                    period={row}
                    container={row.zoneSite || zone}
                    superContainer={
                      isAllReportsList
                        ? resolveSuperContainerForZoneSite(
                            allSuperContainers,
                            row.zoneSite,
                          )
                        : superContainer
                    }
                    containers={isAllReportsList ? allContainers : containers}
                    zone={row.zoneSite || zone}
                    fromAllReports={isAllReportsList}
                  />
                ))
              ) : (
                <div className="table-standard-row table-standard-row--empty">
                  <div
                    className="table-standard-row-cell"
                    style={{ gridColumn: "1 / -1" }}
                  >
                    {t("No results match your search")}
                  </div>
                </div>
              )}
            </StandardTable>
          </Card>
        </div>
      </div>
      <PeriodFilterModal
        isOpen={isPeriodModalOpen}
        onClose={() => setIsPeriodModalOpen(false)}
        onApply={(months, label, startDateIso, endDateIso, presetId) => {
          setPeriodFilter(months);
          setPeriodDisplayLabel(label || getPeriodOptions(t)[0].label);
          setPeriodStartDate(startDateIso ?? null);
          setPeriodEndDate(endDateIso ?? null);
          setCurrentPage(1);
          try {
            // "Since the beginning" should always end at end-of-today, so we don't persist it.
            if (presetId === "sinceBeginning") {
              localStorage.setItem(STORAGE_KEY_PERIOD, "");
              localStorage.setItem(STORAGE_KEY_PERIOD_START, "");
              localStorage.setItem(STORAGE_KEY_PERIOD_END, "");
              localStorage.setItem(STORAGE_KEY_PERIOD_LABEL, label || "");
              return;
            }
            localStorage.setItem(
              STORAGE_KEY_PERIOD,
              months != null ? String(months) : "",
            );
            localStorage.setItem(
              STORAGE_KEY_PERIOD_START,
              startDateIso != null ? startDateIso : "",
            );
            localStorage.setItem(
              STORAGE_KEY_PERIOD_END,
              endDateIso != null ? endDateIso : "",
            );
            localStorage.setItem(
              STORAGE_KEY_PERIOD_LABEL,
              label || getPeriodOptions(t)[0].label || "",
            );
          } catch (_) {
            // ignore
          }
        }}
        currentPeriodFilter={periodFilter}
        currentPeriodStart={periodStartDate}
        currentPeriodEnd={periodEndDate}
      />
      <StatusFilterModal
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        onApply={(status) => {
          setStatusFilter(status);
          const selectedOption = getStatusOptions(t).find(
            (opt) => opt.value === status,
          );
          setStatusDisplayLabel(
            selectedOption?.label || getStatusOptions(t)[0].label,
          );
          setCurrentPage(1);
        }}
        currentStatusFilter={statusFilter}
      />
      <OriginFilterModal
        isOpen={isOriginModalOpen}
        onClose={() => setIsOriginModalOpen(false)}
        onApply={(nextOrigin) => {
          setOriginFilter(nextOrigin && nextOrigin !== "all" ? nextOrigin : "");
          setCurrentPage(1);
        }}
        currentOriginFilter={originFilter || "all"}
        originOptions={originOptions}
      />
      {isAllReportsList ? (
        <ZoneFilterModal
          isOpen={isZoneModalOpen}
          onClose={() => setIsZoneModalOpen(false)}
          onApply={(nextZone) => {
            setZoneFilter(nextZone || "all");
            setCurrentPage(1);
          }}
          currentZoneFilter={zoneFilter}
          zoneOptions={zoneOptions}
        />
      ) : null}
      {isExportingSummary && (
        <PdfExportProgressOverlay
          percent={exportProgress.percent}
          label={exportProgress.label}
        />
      )}
      {modalEdit && (
        <ContainerEdit
          tableData={zone}
          editFormData={formData}
          setEditFormData={setFormData}
          modalEdit={modalEdit}
          setModalEdit={setModalEdit}
          superContainer={!!superContainer}
          containerRight={superContainer ? containers?.[1] : zone}
          setSelectedContainer={() => {}}
          fetchContainer={() => {}}
        />
      )}
    </div>
  );
};

const CharacterizationListPage = () => {
  const { id: routeZoneId } = useParams();
  const { t } = useTranslation();
  const isAllReportsList = !routeZoneId;
  const { mode, isLoading } = useCharacterizationViewMode();

  if (isLoading) {
    return (
      <div className="settings-page-wrapper">
        <PageHeader title={t("Characterizations")} />
        <div className="settings-content-wrapper">
          <InlineLoader height="200px" />
        </div>
      </div>
    );
  }

  if (isAllReportsList && mode === CHARACTERIZATION_VIEW_ZONES) {
    return <Redirect to="/characterization-areas" />;
  }

  return (
    <CharacterizationList landingPath={characterizationLandingPath(mode)} />
  );
};

export default CharacterizationListPage;
